//-tech-debt-react-query;
import { QueryClient, skipToken, useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { deleteItem, fetchItem, fetchItems, postItem, putItem } from "../support/queries";
import { DEFAULT_QUERY_OPTIONS } from "../support/constants";
import { CacheKey } from "../types/cache-keys";
import { v4 as uuid } from 'uuid';
import { queryClient } from "../support/queryClient";

/**
 * Base generic React Query hooks for data fetching and mutations.
 *
 * Encapsulates generic CRUD operations that more business-specific hooks can build upon.
 */

// imperative, cache-first query helper for dynamic/non-hook fetch flows.
const ensureQueryData = async (queryKey: any, queryFn: () => Promise<any>, client: QueryClient = queryClient) => {
  const cacheKey = Array.isArray(queryKey) ? queryKey.map(String) : [String(queryKey)];
  const cached = client.getQueryData(cacheKey);
  if (cached !== undefined) return cached;

  const data = await client.fetchQuery({ queryKey: cacheKey, queryFn });
  return data;
};

const validId = (id: any) => typeof id === 'number' && id !== undefined && id !== null;
const salesforceId = (id: any) => typeof id === 'string' && /^[a-zA-Z0-9]{15,18}$/.test(id);

/**
 * Disabled item queries cannot be manually refetched; enable them with a valid ID first.
 */
const useFetchItem = <T = any>(
  url: string,
  id: string | number | undefined,
  cacheKey: CacheKey | (() => [CacheKey, ...(string | CacheKey | number)[]]),
  params?: object,    // request parameters
  options?: object    // options for useQuery
): UseQueryResult<T, unknown> => {
  const keyBase = typeof cacheKey === "function"
    ? cacheKey().map(String)
    : [String(cacheKey)];
  const enabled = (salesforceId(id) || validId(id)) && ((options && options["enabled"]) ?? true);
  const queryKey = enabled
    ? [...keyBase, String(id)]
    : ["__disabled__", ...keyBase, String(id)];

  return useQuery<T, unknown>({
    queryKey: queryKey,
    queryFn: enabled
      ? () => fetchItem(`${url}/${id}`, params)
      : skipToken,
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
        enabled,
      },
  });
};

const useFetchItems = <T = any>(
  url: string,
  cacheKey: CacheKey | (() => [CacheKey, ...(string | CacheKey | number)[]]),
  params?: object,    // request parameters
  options?: object    // options for useQuery
): UseQueryResult<T, unknown> => {
  const keyBase =
    typeof cacheKey === "function" ? cacheKey().map(String) : [String(cacheKey)];

  const enabled =
    ((options && options["enabled"]) ?? true) &&
    (params ? Object.values(params).every((v) => v !== undefined && v !== null) : true);

  return useQuery({
    queryKey: [...keyBase],
    queryFn: async () => {
      const data = await fetchItems(url, params);

      // Make sure we always return an array so we don't need to check for that in the UI
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (Array.isArray(data.items)) return data.items;
      return [data];
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
        enabled,
      },
  });
};

const useAddItemMutation = <T>(
  url: string,
  queryKeyFn: (added: T) => [CacheKey, ...(string | CacheKey | number)[]],
  extraMutationOptions?: Partial<UseMutationOptions<T, Error, Omit<T, 'id'>, any>>,
  params: Record<string, any> = null
) => {
  const queryClient = useQueryClient();

  return useMutation<
    T,
    Error,
    Omit<T, 'id'>,
    {
      previous: T[] | undefined;
      arrayExists: boolean;
      arrayQueryKey: string[];
    }
  >({
    mutationFn: (payload) => postItem(url, payload, params),
    onMutate: async (created, mutationContext) => {
      const arrayQueryKey = queryKeyFn(created as T).map(String);
      await queryClient.cancelQueries({ queryKey: arrayQueryKey });
      const previous = queryClient.getQueryData<T[]>(arrayQueryKey) || undefined;
      const arrayExists = queryClient.getQueryData<T[]>(arrayQueryKey) !== undefined;
      queryClient.setQueryData<T[]>(arrayQueryKey, [
        ...(previous ?? []),
        { ...(created as T), id: uuid() },
      ]);

      extraMutationOptions?.onMutate?.(created, mutationContext);
      return { previous, arrayExists, arrayQueryKey };
    },
    onSuccess: (response, _variables, context, mutationContext) => {
      queryClient.setQueryData<T[]>(context.arrayQueryKey, [...(context?.previous || []), response]);

      // create a single entry for new item
      const id = (response as any)?.id;
      if (
        context.arrayQueryKey &&
        context.arrayQueryKey.length > 0 &&
        (validId(id) || salesforceId(id))
      ) {
        const singleKey = [...context.arrayQueryKey, String(id)];
        queryClient.setQueryData(singleKey, response);
        queryClient.prefetchQuery({
          queryKey: singleKey,
          queryFn: () => fetchItem(`${url}/${id}`, params),
        });
      }

      extraMutationOptions?.onSuccess?.(response, _variables, context, mutationContext);
    },
    onError: (_error, _variables, context, mutationContext) => {
      if (context?.arrayExists) {
        queryClient.setQueryData(context.arrayQueryKey, context.previous);
      } else if (context) {
        queryClient.removeQueries({ queryKey: context.arrayQueryKey });
      }
      extraMutationOptions?.onError?.(_error, _variables, context, mutationContext);
    },
    onSettled: (_response, _error, _variables, context, mutationContext) => {
      if (context) queryClient.invalidateQueries({ queryKey: context.arrayQueryKey });
      extraMutationOptions?.onSettled?.(_response, _error, _variables, context, mutationContext);
    },
  })
};

// Generic update mutation to handle caching of individual T item using [...string[], id], and T[] using [...string[]].
const useUpdateItemMutation = <InputType extends { id: string | number }, OutputType extends { id: string | number } = InputType>(
  url: string,
  queryKeyFn: (added: InputType) => [CacheKey, ...(string | CacheKey | number)[]],
  extraMutationOptions?: Partial<UseMutationOptions<OutputType, Error, InputType, any>>,
  params?: Record<string, any>
) => {
  const queryClient = useQueryClient();

  return useMutation<
    OutputType,
    Error,
    InputType,
    {
      previous: OutputType | undefined;
      previousArray: OutputType[];
      singleExists: boolean;
      arrayExists: boolean;
      queryKey: string[];
      arrayKey: string[] | null;
    }
  >({
    mutationFn: (payload) => putItem(url, payload, params),
    onMutate: async (updated, mutationContext) => {
      const queryKey = queryKeyFn(updated).map(String);
      const arrayKey = queryKey.length > 1 ? queryKey.slice(0, -1) : null;

      await queryClient.cancelQueries({ queryKey });
      if (arrayKey) await queryClient.cancelQueries({ queryKey: arrayKey });

      const previous = queryClient.getQueryData<OutputType>(queryKey) || undefined;
      const previousArray = arrayKey ? queryClient.getQueryData<OutputType[]>(arrayKey) || [] : [];

      const singleExists = queryClient.getQueryData<OutputType>(queryKey) !== undefined;
      const arrayExists = arrayKey
        ? queryClient.getQueryData<OutputType[]>(arrayKey) !== undefined
        : false;

      if (singleExists) {
        queryClient.setQueryData<OutputType>(queryKey, (prev) => ({ ...prev, ...updated }));
      }
      if (arrayExists) {
        queryClient.setQueryData<OutputType[]>(arrayKey, () =>
          previousArray.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
        );
      }

      extraMutationOptions?.onMutate?.(updated, mutationContext);
      return { previous, previousArray, singleExists, arrayExists, queryKey, arrayKey };
    },
    onSuccess: (response, _variables, context, mutationContext) => {
      if (context.singleExists) {
        queryClient.setQueryData<OutputType>(context.queryKey, response);
      }
      if (context.arrayExists) {
        queryClient.setQueryData<OutputType[]>(context.arrayKey, () =>
          context.previousArray.map((item) => (item.id === response.id ? response : item))
        );
      }
      extraMutationOptions?.onSuccess?.(response, _variables, context, mutationContext);
    },
    onError: (_error, _variables, context, mutationContext) => {
      if (context?.singleExists && context.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
      if (context?.arrayExists) {
        queryClient.setQueryData(context.arrayKey, context.previousArray);
      }
      extraMutationOptions?.onError?.(_error, _variables, context, mutationContext);
    },
    onSettled: (_response, _error, _variables, context, mutationContext) => {
      if (context) queryClient.invalidateQueries({ queryKey: context.queryKey });
      if (context?.arrayKey) queryClient.invalidateQueries({ queryKey: context.arrayKey });
      extraMutationOptions?.onSettled?.(_response, _error, _variables, context, mutationContext);
    },
  })
};

// Generic delete mutation to handle caching of individual T item using [string, id], and T[] using [string].
const useDeleteItemMutation = <T extends { id: string | number}>(
  url: string,
  queryKeyFn: (added: T) => [CacheKey, ...(string | CacheKey | number)[]],
  extraMutationOptions?: Partial<UseMutationOptions<boolean, Error, T, any>>
) => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean,
    Error,
    T,
    {
      previous: T | undefined;
      previousArray: T[];
      singleExists: boolean;
      arrayExists: boolean;
      queryKey: string[];
      arrayKey: string[] | null;
    }
  >({
    mutationFn: ({ id }) => deleteItem(`${url}/${id}`),
    onMutate: async (deleted) => {
      const queryKey = queryKeyFn(deleted).map(String);
      const arrayKey = queryKey.length > 1 ? queryKey.slice(0, -1) : null;

      await queryClient.cancelQueries({ queryKey });
      if (arrayKey) await queryClient.cancelQueries({ queryKey: arrayKey });

      const previousArray = arrayKey ? queryClient.getQueryData<T[]>(arrayKey) || [] : [];
      const previous = queryClient.getQueryData<T>(queryKey) || undefined;

      const singleExists = queryClient.getQueryData<T>(queryKey) !== undefined;
      const arrayExists = arrayKey ? queryClient.getQueryData<T[]>(arrayKey) !== undefined : false;

      if (singleExists) {
        queryClient.removeQueries({ queryKey, exact: true });
      }
      if (arrayExists && previousArray) {
        queryClient.setQueryData<T[]>(arrayKey, () =>
          previousArray.filter((item) => item.id !== deleted.id)
        );
      }

      return { previous, previousArray, singleExists, arrayExists, queryKey, arrayKey };
    },
    onError: (_error, _variables, context, mutationContext) => {
      if (context?.singleExists && context.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
      if (context?.arrayExists) {
        queryClient.setQueryData(context.arrayKey, context.previousArray);
      }
      extraMutationOptions?.onError?.(_error, _variables, context, mutationContext);
    },
    onSuccess: (_data, _variables, _context, mutationContext) => {
      extraMutationOptions?.onSuccess?.(_data, _variables, _context, mutationContext);
    },
    onSettled: (_response, _error, _variables, context, mutationContext) => {
      if (context) queryClient.invalidateQueries({ queryKey: context.queryKey });
      if (context?.arrayKey) queryClient.invalidateQueries({ queryKey: context.arrayKey });
      extraMutationOptions?.onSettled?.(_response, _error, _variables, context, mutationContext);
    },
  })
};

export {
  ensureQueryData,
  salesforceId,
  validId,

  useFetchItem,
  useFetchItems,

  useAddItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
}